import React, { useState } from "react";
import { CheckCircle2, XCircle, ShieldCheck, Clock, ShieldX, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

/**
 * Marketing Manager approval block — renders on a project detail page.
 * - For the managing MM: shows "Aanvaarden" / "Afwijzen" buttons when approval is still pending,
 *   or a confirmation card once they've decided.
 * - For everyone else (admin, agent): shows a read-only status badge.
 *
 * Props:
 *  - req: the request document (must include requires_mm_approval, mm_approved, mm_*)
 *  - currentUser: the logged-in user
 *  - onUpdated: callback(updatedRequest)
 */
export default function MMApprovalPanel({ req, currentUser, onUpdated }) {
    const [submitting, setSubmitting] = useState(false);
    const [declineOpen, setDeclineOpen] = useState(false);
    const [reason, setReason] = useState("");

    if (!req?.requires_mm_approval) return null;

    const isMM = currentUser?.account_type === "marketing_manager";
    // The MM may approve only if they manage the owner agent.
    const canDecide = isMM && (currentUser?.managed_agent_ids || []).includes(req.user_id);
    const status = req.mm_approved; // null | true | false

    const approve = async () => {
        setSubmitting(true);
        try {
            const { data } = await api.post(`/requests/${req.id}/mm-approve`, { approve: true });
            toast.success("Aanvraag aanvaard");
            onUpdated?.(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Goedkeuring mislukt");
        }
        setSubmitting(false);
    };

    const decline = async () => {
        if (!reason.trim()) {
            toast.error("Geef een reden op");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post(`/requests/${req.id}/mm-approve`, { approve: false, reason: reason.trim() });
            toast.success("Aanvraag afgewezen");
            onUpdated?.(data);
            setDeclineOpen(false);
            setReason("");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Afwijzen mislukt");
        }
        setSubmitting(false);
    };

    // Status badge variants
    let icon = Clock;
    let label = "Wacht op MM-goedkeuring";
    let bg = "bg-amber-500/10 border-amber-500/30 text-amber-700";
    if (status === true) {
        icon = ShieldCheck;
        label = `Aanvaard door ${req.mm_approved_by_name || "MM"}`;
        bg = "bg-emerald-500/10 border-emerald-500/30 text-emerald-700";
    } else if (status === false) {
        icon = ShieldX;
        label = `Afgewezen door ${req.mm_approved_by_name || "MM"}`;
        bg = "bg-red-500/10 border-red-500/30 text-red-700";
    }
    const Icon = icon;

    return (
        <div className={`rounded-md border p-4 ${bg}`} data-testid="mm-approval-panel">
            <div className="flex items-start gap-3">
                <Icon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-90">Marketing manager goedkeuring</div>
                    <div className="text-sm font-medium mt-1">{label}</div>
                    {status === false && req.mm_decline_reason && (
                        <div className="mt-2 text-xs bg-black/30 rounded-sm p-2" data-testid="mm-decline-reason">
                            <span className="opacity-70">Reden: </span>{req.mm_decline_reason}
                        </div>
                    )}
                    {status === true && req.mm_approved_at && (
                        <div className="mt-1 text-[11px] opacity-70">
                            {new Date(req.mm_approved_at).toLocaleString("nl-BE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </div>
                    )}
                    {canDecide && status !== true && (
                        <div className="mt-3 flex items-center gap-2">
                            <Button
                                onClick={approve}
                                disabled={submitting}
                                className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold rounded-sm h-9 px-4 active:scale-[0.98]"
                                data-testid="mm-approve-btn"
                            >
                                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-1.5" /> Aanvaarden</>}
                            </Button>
                            {status === null && (
                                <Button
                                    onClick={() => setDeclineOpen(true)}
                                    disabled={submitting}
                                    variant="ghost"
                                    className="text-red-700 hover:text-red-700 hover:bg-red-500/10 border border-red-500/30 rounded-sm h-9 px-4"
                                    data-testid="mm-decline-btn"
                                >
                                    <XCircle className="h-4 w-4 mr-1.5" /> Afwijzen
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <Dialog open={declineOpen} onOpenChange={(o) => { if (!submitting) setDeclineOpen(o); }}>
                <DialogContent className="bg-[#FFFFFF] border-zinc-200 text-zinc-950 max-w-md" data-testid="mm-decline-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display font-bold text-xl tracking-tight">Aanvraag afwijzen</DialogTitle>
                        <p className="text-sm text-zinc-500">Geef een korte reden — de admin krijgt deze in de notificatie en beslist finaal.</p>
                    </DialogHeader>
                    <Textarea
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        rows={4}
                        placeholder="bv. Pand nog niet klaar / publicatie-budget overschreden / klant trekt zich terug…"
                        className="bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#E5A93D]"
                        data-testid="mm-decline-reason-input"
                    />
                    <DialogFooter className="gap-2">
                        <Button variant="ghost" onClick={() => { setDeclineOpen(false); setReason(""); }} disabled={submitting} className="text-zinc-500 hover:text-zinc-950">
                            Annuleren
                        </Button>
                        <Button onClick={decline} disabled={submitting} className="bg-red-500 hover:bg-red-400 text-zinc-950 font-bold rounded-sm" data-testid="mm-decline-confirm">
                            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Afwijzen bevestigen"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
